import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
	TransitionChild
} from "@headlessui/react";
import React, { Fragment, useEffect, useRef } from "react";

interface SidePanelProps {
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	fullWidth?: boolean;
	children: React.ReactNode;
	slideFrom?: "right" | "bottom";
	closeOnOutsideClick?: boolean;
}

export default function SidePanel({
	isOpen,
	onClose,
	title,
	fullWidth = false,
	children,
	slideFrom,
	closeOnOutsideClick = true
}: SidePanelProps) {
	const initialFocusRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (isOpen && initialFocusRef.current) {
			const timer = setTimeout(() => {
				initialFocusRef.current?.focus();
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [isOpen]);

	const handleDialogClose = closeOnOutsideClick ? onClose : () => {};

	return (
		<Dialog
			open={isOpen}
			onClose={handleDialogClose}
			className="relative z-50"
			initialFocus={initialFocusRef}
		>
			<TransitionChild
				as={Fragment}
				enter="transition-opacity ease-linear duration-300"
				enterFrom="opacity-0"
				enterTo="opacity-100"
				leave="transition-opacity ease-linear duration-100"
				leaveFrom="opacity-100"
				leaveTo="opacity-0"
			>
				<DialogBackdrop
					transition
					className="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-closed:opacity-0"
				/>
			</TransitionChild>

			<div className="fixed inset-0 overflow-hidden">
				<div className="absolute inset-0 overflow-hidden">
					<div
						className={`pointer-events-none fixed ${
							slideFrom === "bottom"
								? "inset-x-0 bottom-0 top-0.5 justify-center"
								: "inset-y-0 right-0 pl-10"
						} flex max-w-full`}
					>
						<TransitionChild
							as={Fragment}
							enter="transform transition ease-out duration-500"
							enterFrom={
								slideFrom === "bottom" ? "translate-y-full" : "translate-x-full"
							}
							enterTo="translate-y-0 translate-x-0"
							leave="transform transition ease-in duration-400"
							leaveFrom="translate-y-0 translate-x-0"
							leaveTo={
								slideFrom === "bottom" ? "translate-y-full" : "translate-x-full"
							}
						>
							<DialogPanel
								transition
								className={`pointer-events-auto relative w-screen transform ${
									fullWidth ? "w-screen max-w-full" : "w-screen max-w-md"
								} ${isOpen ? "translate-x-0" : "translate-x-full"}`}
							>
								<div
									ref={initialFocusRef}
									tabIndex={-1}
									className="outline-none"
								/>

								<div className="absolute top-0 right-0 -ml-8 flex pt-4 pr-2 duration-500 ease-in-out data-closed:opacity-0 sm:-ml-10 sm:pr-4">
									<button
										type="button"
										onClick={onClose}
										className="relative rounded-md text-gray-300 hover:text-white focus:ring-2 focus:ring-white border-gray-200 bg-[#002666]"
									>
										<span className="absolute -inset-2.5" />
										<span className="sr-only">Close panel</span>X
									</button>
								</div>

								<div className="flex h-full flex-col bg-white shadow-xl">
									<div className="py-6 px-4 sm:px-6 bg-blue-darkFynxt">
										<DialogTitle className="text-lg font-semibold text-white">
											{title || "Panel"}
										</DialogTitle>
									</div>

									<div className="relative flex-1 overflow-y-auto px-4 sm:px-6 mt-6">
										{children}
									</div>
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</div>
		</Dialog>
	);
}
